import { useCallback } from "react";
import { Paperclip } from "lucide-react";
import { t, dateLocale } from "@/lib/i18n";
import type { LogEntry, LogLevel } from "@/lib/types";
import { cn } from "@/lib/utils";
import { LevelBadge } from "./LevelBadge";

type LogTableProps = {
  logs: LogEntry[];
  onSelect: (log: LogEntry) => void;
  selectedId?: number | null;
  /** Aktiver Suchbegriff — Treffer werden in der Nachricht hervorgehoben. */
  highlight?: string;
};

/**
 * Farbige Kante links an der Mobil-Karte: Level auf einen Blick beim Scrollen,
 * ohne das Badge lesen zu müssen. Auf Desktop (Tabellenzeile) ausgeblendet.
 */
const LEVEL_EDGE: Record<LogLevel, string> = {
  TRACE: "border-l-border",
  DEBUG: "border-l-border",
  INFO: "border-l-primary/40",
  WARN: "border-l-warning",
  ERROR: "border-l-destructive",
  FATAL: "border-l-destructive",
};

function escapeRegExp(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/**
 * Hebt Vorkommen des Suchbegriffs im Text hervor. '*' im Begriff ist das
 * Wildcard der Suche und teilt ihn in einzeln markierte Segmente
 * ("conn*timeout" markiert "conn" und "timeout").
 */
function Highlight({ text, term }: { text: string; term?: string }) {
  const parts = (term ?? "").split("*").map((p) => p.trim()).filter(Boolean);
  if (parts.length === 0) return <>{text}</>;
  // Eine Capture-Gruppe über alle Segmente → beim split stehen die Treffer
  // an den ungeraden Indizes.
  const re = new RegExp(`(${parts.map(escapeRegExp).join("|")})`, "gi");
  const segments = text.split(re);
  if (segments.length === 1) return <>{text}</>;
  return (
    <>
      {segments.map((seg, i) =>
        i % 2 === 1 ? (
          <mark key={i} className="rounded-[2px] bg-primary/25 text-inherit">
            {seg}
          </mark>
        ) : (
          seg
        ),
      )}
    </>
  );
}

/** Uhrzeit; Millisekunden nur auf Desktop — mobil kostet das Platz in der Meta-Zeile. */
function formatTime(ts: string, withMillis: boolean): string {
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return ts;
  const opts: Intl.DateTimeFormatOptions = {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  };
  if (withMillis) {
    (opts as Intl.DateTimeFormatOptions & { fractionalSecondDigits: number }).fractionalSecondDigits = 3;
  }
  return d.toLocaleTimeString(dateLocale(), opts);
}

export function LogTable({ logs, onSelect, selectedId, highlight }: LogTableProps) {
  const onKey = useCallback(
    (e: React.KeyboardEvent, log: LogEntry) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        onSelect(log);
      }
    },
    [onSelect],
  );

  if (logs.length === 0) {
    return (
      <div className="rounded-md border border-dashed border-border bg-card p-10 text-center text-sm text-muted-foreground">
        {t("Keine Logs für die aktuellen Filter.")}
      </div>
    );
  }

  return (
    <div className="overflow-hidden rounded-md border border-border bg-card">
      {/* Spaltenkopf nur auf Desktop — die Mobil-Karten sind selbsterklärend. */}
      <div className="hidden grid-cols-[140px_70px_140px_1fr] gap-2 border-b border-border bg-muted/40 px-3 py-2 font-sans text-[11px] font-medium uppercase tracking-wide text-muted-foreground md:grid">
        <span>{t("Zeit")}</span>
        <span>{t("Level")}</span>
        <span>{t("Client / Service")}</span>
        <span>{t("Nachricht")}</span>
      </div>
      <div className="font-mono text-xs">
        {logs.map((log, i) => {
          const key = log.id ?? `${log.timestamp}-${i}`;
          const isSelected = selectedId != null && log.id === selectedId;
          return (
            <div
              key={key}
              role="button"
              tabIndex={0}
              onClick={() => onSelect(log)}
              onKeyDown={(e) => onKey(e, log)}
              className={cn(
                "cursor-pointer border-b border-border outline-none transition-colors last:border-b-0 hover:bg-muted/40 focus:bg-muted/60",
                // Mobil: Karte — Meta-Zeile oben, Nachricht darunter voll umbrechend.
                "flex flex-col gap-1 border-l-[3px] px-3 py-2.5",
                LEVEL_EDGE[log.level],
                // Desktop: klassische Tabellenzeile.
                "md:grid md:grid-cols-[140px_70px_140px_1fr] md:items-center md:gap-2 md:border-l-0 md:px-3 md:py-1.5",
                isSelected && "bg-accent/40",
              )}
            >
              {/*
                Meta-Block: mobil eine Zeile (Badge · Client · Zeit rechts),
                auf Desktop per `display: contents` drei eigenständige Grid-Zellen
                in Tabellenreihenfolge (Zeit · Badge · Client).
              */}
              <div className="flex min-w-0 items-center gap-2 text-[11px] md:contents md:text-xs">
                <span className="order-last ml-auto shrink-0 text-muted-foreground tabular-nums md:order-none md:ml-0">
                  <span className="md:hidden">{formatTime(log.timestamp, false)}</span>
                  <span className="hidden md:inline">{formatTime(log.timestamp, true)}</span>
                </span>
                <LevelBadge level={log.level} />
                <span className="min-w-0 truncate text-muted-foreground">
                  {log.client_id}
                  {log.service ? <span className="text-muted-foreground/60"> · {log.service}</span> : null}
                </span>
              </div>

              <span className="flex min-w-0 items-start gap-1.5 md:items-center">
                {log.payload_mime ? (
                  <Paperclip
                    className="mt-0.5 h-3 w-3 shrink-0 text-muted-foreground md:mt-0"
                    aria-label={t("Payload angehängt")}
                  />
                ) : null}
                {/*
                  Mobil: bis zu 6 Zeilen umbrechen (line-clamp), Zeilenumbrüche
                  aus der Nachricht bleiben erhalten. Desktop: eine Zeile mit
                  Ellipse — der volle Text steht im Detail-Drawer.
                */}
                <span className="min-w-0 whitespace-pre-wrap break-words text-[13px] leading-snug line-clamp-6 md:block md:truncate md:text-xs md:leading-normal">
                  <Highlight text={log.message} term={highlight} />
                </span>
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
